import { Button, message, Spin } from "antd";
import "./style.css";
import { TOnBoardSymptomsProps } from "@stores/interfaces";
import { useTranslation } from "react-i18next";
import AnimationCard from "./AnimationCard";
import { useTypedDispatch, useTypedSelector } from "@stores/index";
import { useEffect, useState } from "react";
import { getFunctionalGoals, IGetDetails } from "@stores/functionalGoals";
import { savedFunctionalGoal, updateFunctionalGoal } from "@stores/onBoard/onBoard";
import { FEATURES_MOCK } from "./FeaturesMock";
import { PLANS } from "@stores/constants";
import { FUNCTIONAL_GOAL_IDS } from "@pages/Settings/contentManagerMockData";
import { getFunctionalGoalById } from "@stores/settings/setings";

export interface FeatureProps {
  title: string;
  backgroundStyle: string;
  hoverBackgroundStyle: string;
  description: string;
  imageSrc: string;
}

export default function FunctionalGoals(props: TOnBoardSymptomsProps) {
  const { setActiveStep, setProgressPercent ,navigatorDirection,  setNavigatorDirection} = props;
  const { t } = useTranslation();
  const dispatch = useTypedDispatch();
  const savedFunctionalGoals = useTypedSelector((state) => state.onBoard.onBoard.savedFunctionalGoals);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [selectedCards, setSelectedCards] = useState<Record<string, number[]>>({});
  const { user, selectedUser } = useTypedSelector((state) => ({
    user: state.user,
    selectedUser: state.contacts.main.selectedUser,
  }))
  const [functionalGoalList, setFunctionalGoalList] = useState<IGetDetails>();
  const getFunctionalGoalsData = useTypedSelector(state => state.functionalGoals.functionalGoals)
  const { functionalGoals } = useTypedSelector(state => state.settings.settings)
   const savedUserPlans = useTypedSelector((state) => state.settings.settings.savedUserPlans)
  const planType = savedUserPlans?.planType;

  useEffect(() => {
    if (planType === PLANS.SCREENING) {
      if (navigatorDirection === 'forward') {
        setActiveStep!(7);
        setProgressPercent(70);
      } else if (navigatorDirection === 'backward') {
        setActiveStep!(5);
        setProgressPercent(50);
      }
    }
  }, [planType, navigatorDirection]);

  useEffect(() => {
    fetchFunctionalGoals();
  }, []);

  useEffect(() => {
    const updatedGoals = getFunctionalGoalsData?.data.map(goal => {
      const matched = functionalGoals?.find(
        fg => fg?.functionalGoalId === goal?.id
      );
      const matchedMock = FEATURES_MOCK.find((item) => item.id === Number(goal?.id));

      return {
        id: goal?.id,
        attributes: {
          ...goal?.attributes,
          name: matched?.title || goal.attributes?.name || matchedMock?.name,
          description: matched?.description || t(`Patient.data.functionalGoals.description-${goal?.id}`) || "",
          thumbnail: matched?.thumbnail || matchedMock?.imageSrc || "",
        },
      };
    });

    setFunctionalGoalList({
      data: updatedGoals,
      meta: getFunctionalGoalsData?.meta!,
    });
  }, [getFunctionalGoalsData, functionalGoals]);

  useEffect(() => {
    let savedIds: number[] = [];
    if (savedFunctionalGoals?.functionalGoalsIds?.length) {
      savedIds = savedFunctionalGoals?.functionalGoalsIds;
    } else if (selectedUser?.functionalGoals?.length) {
      const lastIndex = selectedUser.functionalGoals.length - 1;
      savedIds = selectedUser.functionalGoals[lastIndex]?.functionalGoalsIds || [];
    }
    if (savedIds.length > 0 && Array.isArray(functionalGoalList?.data)) {
      const preSelectedCards: Record<string, number[]> = {};
  
      functionalGoalList.data.forEach(({ id, attributes }) => {
        if (savedIds.includes(id)) {
          preSelectedCards[attributes.name] = [id];
        }
      });
  
      setSelectedCards(preSelectedCards);
    } else {
      setSelectedCards({});
    }
  }, [savedFunctionalGoals, selectedUser, functionalGoalList]);
  
  
  useEffect(() => {
    fetchingFunctionalGoalData();
  }, [])

  const fetchFunctionalGoals = async () => {
    for (const id of FUNCTIONAL_GOAL_IDS) {
      await dispatch(getFunctionalGoalById(id));
    }
  };

  const fetchingFunctionalGoalData = async () => {
    await dispatch(getFunctionalGoals());
    setIsDataLoaded(true);
  };

  const handleNextClick = async () => {
    const functionalGoalsIds = Object.values(selectedCards).flat();
    if (functionalGoalsIds.length === 0) {
      message.error(t("Patient.data.virtualEvaluation.associatedSymptoms.pleaseSelectAtLeastOneOption"));
      return;
    }
    const payload = {
      functionalGoalsIds,
    };
    savedFunctionalGoals.length > 0 ? await dispatch(updateFunctionalGoal({ userId: user?.id, payload })) : await dispatch(savedFunctionalGoal({ userId: user?.id, payload }))
    setNavigatorDirection('forward')
    setActiveStep!(7);
    setProgressPercent(70);
  };

  const handleCardSelect = (title: string) => {
    setSelectedCards((prev) => {
      const newState = { ...prev };
      if (newState[title]) {
        delete newState[title];
      } else {
        const matchedGoal = functionalGoalList?.data?.find(
          (goal) => goal.attributes.name === title
        );
  
        if (matchedGoal) {
          newState[title] = matchedGoal.id;
        }
      }
      return newState;
    });
  };
   
  if (planType === PLANS.SCREENING) return null;

  if (!isDataLoaded) {
    return <Spin className="spin-css" tip="Loading" size="large" />;
  }

	const nextButtonStyle = {
		height: '43px',
		background: 'var(--onboard-button-color)',
		color: '#fff',
		width: '308px',
    border: 'none',
	};

  return (
    <>
      <div className="container functional-container">
        <div className="whole-day-header-container functional-sub-container">
          <h1 className="functional-goal-heading">
            {t("Patient.data.onboard.functionalGoals")}
          </h1>
          <h3 className="functional-goal-sub-heading">
            {t("Patient.data.onboard.defineObj")}
          </h3>
        </div>
        <div className="functional-card-div">
          {functionalGoalList?.data?.map((feature, index) => (
            <AnimationCard
              key={index}
              setActiveStep={setActiveStep}
              setProgressPercent={setProgressPercent}
              {...feature}
              handleCardSelection={handleCardSelect}
              isSelected={!!selectedCards[feature.attributes?.name]}
            />
          ))}
        </div>
        <div className="back-button-div-css">
          <Button className="back-button-inside-css" onClick={() => {setNavigatorDirection('backward'); setActiveStep!(5)}} 
           size="large" block style={nextButtonStyle}>
            {t("Patient.data.onboard.back")}
          </Button>
          <Button size="large" className="back-button-inside-css" block style={nextButtonStyle} onClick={handleNextClick}>
            {t("Patient.data.onboard.save")}
          </Button>
        </div>
      </div>
    </>
  );
}